<script setup>
import {ref, computed, onMounted} from 'vue'
import ProgressBar from '@/vue/common/ProgressBar'
import Button from "./Button.vue"
import api from '@/services/api'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    controller: String,
    mimeTypes: String,
    multiple: Boolean,
    setFile: Function
})

const appStore = useAppStore()

// --- State ---

const dropzone = ref(null)
const fileInput = ref(null)
const files = ref([])

// --- Computed ---

const fileStr = computed(() => props.multiple ? 'files' : 'file')

const acceptedMimeTypes = computed(() => {
    return props.mimeTypes ? props.mimeTypes.split(',') : []
})

// --- Methods ---

/**
 * Replaces the 'humanFileSize' mixin with a local helper
 */
const formatSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024
    if (Math.abs(bytes) < thresh) return bytes + ' B'
    const units = si
        ? ['kB', 'MB', 'GB', 'TB']
        : ['KiB', 'MiB', 'GiB', 'TiB']
    let u = -1
    const r = 10 ** dp
    do {
        bytes /= thresh
        ++u
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)
    return bytes.toFixed(dp) + ' ' + units[u]
}

const chooseFile = () => fileInput.value?.click()

const applyDropzoneEvents = () => {
    const el = dropzone.value
    if (!el) return

    el.ondragenter = () => el.classList.add('active')
    el.ondragleave = () => el.classList.remove('active')
    el.ondragover = (e) => {
        e.preventDefault()
        el.classList.add('active')
    }
    el.ondrop = (e) => {
        e.preventDefault()
        el.classList.remove('active')
        const data = e.dataTransfer || e.originalEvent.dataTransfer
        uploadFiles(data.files)
    }
}

const validateFileNames = async () => {
    const fileNames = files.value.map(o => o.name)

    try {
        const response = await api.get('api/component/files', {
            params: {fileName: fileNames}
        })

        const existingItems = response.data.items || []
        existingItems.forEach(o => {
            const match = files.value.find(f => f.name === o.fileName)
            if (match) match.error = 'File exists'
        })
    } catch (err) {
        console.error("Filename validation failed", err)
    }
}

const sendToApi = async (formData, fileData) => {
    let controller = props.controller || 'api/FilesController/upload'

    try {
        const response = await api.post(`${appStore.apiEndpoint}/${controller}`, formData, {
            onUploadProgress: (e) => {
                fileData.percentage = (e.loaded / e.total * 100 | 0)
            },
        })

        if (props.setFile) props.setFile(response.data)
    } catch (err) {
        fileData.error = "Upload failed"
    }
}

const fileUploadLoop = (filesArray) => {
    filesArray.forEach((file) => {
        let fileData = files.value.find(o => o.name === file.name)

        if (fileData && fileData.error === undefined) {
            let formData = new FormData()
            formData.append('file', file)
            fileData.percentage = 0
            sendToApi(formData, fileData)
        }
    })
}

const uploadFiles = async (rawFiles) => {
    const filesArray = Array.from(rawFiles)

    filesArray.forEach((file) => {
        let fileData = {
            name: file.name,
            size: file.size,
            type: file.type,
            percentage: undefined,
            error: undefined
        }

        if (acceptedMimeTypes.value.length && !acceptedMimeTypes.value.includes(file.type)) {
            fileData.error = 'Mimetype not accepted'
        }

        files.value.push(fileData)
    })

    await validateFileNames()
    fileUploadLoop(filesArray)
}

const onChangeFileInput = () => {
    if (fileInput.value?.files.length) {
        uploadFiles(fileInput.value.files)
    }
}

// --- Lifecycle ---

onMounted(() => {
    applyDropzoneEvents()
})
</script>

<template>
    <div ref="dropzone" class="well dropzone">
        <div class="d-flex align-items-center justify-content-center">
            Drag {{ fileStr }} here, or

            <Button class="ms-2 btn-sm" :default-class="'btn-secondary'" @click="chooseFile">
                Browse
            </Button>

            <input
                ref="fileInput"
                :multiple="multiple"
                class="d-none"
                type="file"
                @change="onChangeFileInput"
            >
        </div>

        <div v-if="files.length" class="mt-3">
            <div class="list-group">
                <div v-for="file in files" :key="file.name" class="list-group-item">
                    <p class="file-info">
                        <span>{{ file.name }}</span>

                        <code v-if="file.percentage >= 0">{{ formatSize(file.size) }}</code>
                        <span v-if="file.error" class="badge bg-danger">{{ file.error }}</span>
                        <span v-if="file.percentage >= 0">{{ file.percentage }}%</span>
                    </p>

                    <ProgressBar v-if="file.percentage >= 0" :percentage="file.percentage"/>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dropzone {
    position: relative;
    margin: -10px 0 0 -10px;
    padding: 10px;
    border-radius: 5px;
    transition: background-color 0.2s;
}

.dropzone.active {
    background-color: #e3e3e3;
}

.file-info {
    display: flex;
    align-items: center;
}

.file-info > * {
    margin-left: 5px;
}

.file-info > :first-child {
    flex: auto;
    margin-left: 0;
}
</style>